import React, { useState, useEffect, useMemo, CSSProperties, useContext } from 'react'
import MaestroAdCard from "./MaestroAdCard"
import { Tag } from "elements/TagSymphony"
import ButtonSymphony from "elements/ButtonSymphony"
import TagSymphony from "elements/TagSymphony"
import { clsx, makeKey } from "modules/Utils"
import LoadingBar from "elements/LoadingBar"
import useCampaignBuilder, { CreativeSelectorPost } from "Hooks/useCampaignBuilder"
import TabsSymphony, { TabItem } from "elements/TabsSymphony"
import TabSymphony from "elements/TabSymphony"
import InputSymphony from "elements/InputSymphony"
import { searchMaestroFacebookInterests_api, searchSimilarArtists_api, SpotifyArtistSearchResponse } from "api/facebookApi"
import { searchTiktokTargetingInterests_api } from "api/tiktokApi"
import { DataFBQueryAdInterest, FBCustomAudience, SearchPlatformInterestsResult, TargetingAudienceGroup } from "modules/Types"
import millify from "millify"
import useBrand, { BrandExistsState } from "Hooks/useBrand"
import ItemSelector, { ItemSelectorAction } from "elements/ItemSelector"
import CardSymphony from "elements/CardSymphony"
import { MaestroLoadingMessage } from "pages/MarketingPage/MaestroBanner"
import Banner from "elements/Banner"
import Sty from "modules/Sty"
import { findRelatedArtistsForSpotifyId_api } from "api/spotifyApi"
import { SHARED_AUDIENCE_CONFIGS, PLAYBOOK_TYPE_INFO, AdPlatform, PlanType, AdConnectionType } from 'modules/Const';
import { FbSuggestion, MediaType } from 'modules/Types';
import Colors from 'modules/Colors'
import Icon from 'elements/Icon'
import { queryFB } from 'helpers/FB'
import useFacebookConnections from 'Hooks/useFacebookConnections'
import AdConnectionModal from 'components/shareable/AdConnectionModal'
import { UpgradeModalContext } from "Hooks/UpgradeModalContext"
import UpgradeToUnlockCTA from 'pages/WebsitePage/components/CustomizeWebsite/components/UpgradeToUnlockCTA'

// Add a type extension for TikTok interests that includes category
interface TikTokSearchPlatformInterestsResult extends SearchPlatformInterestsResult {
    category?: string;
}

const AD_WILL_TARGET = [
    {
        id: 1,
        title: 'TikTok Best Practice Audiences',
    }
]

const audiencesData = [
    {
        id: 1,
        title: '🎧 Trendy, Hip-hop Gen Z Music Lovers',
        description: 'This is based on your post content, and it\'s the right targeting for you! Includes audiences like:',
        tags: [
            { id: 1, title: 'Hip-hop Enthusiasts' },
            { id: 2, title: 'Gen Z' },
            { id: 3, title: 'Trending Music' },
            { id: 4, title: 'Music Lovers' },
            { id: 5, title: 'Urban Culture' },
        ]
    },
    {
        id: 2,
        title: '🤖 HyperPop Lovers Gen-Z',
        description: 'Target young audiences who love experimental electronic music, glitch pop and futuristic sounds. Perfect for reaching digital natives who embrace avant-garde music.',
        tags: [
            { id: 1, title: 'Hyperpop Fans' },
            { id: 2, title: 'Gen Z' },
            { id: 3, title: 'Electronic Music' },
            { id: 4, title: 'Digital Culture' },
            { id: 5, title: 'Experimental Music' },
        ]
    },
    {
        id: 3,
        title: '💿 New Era Music Hiphop Heads',
        description: 'Connect with modern hip-hop enthusiasts who follow the latest trends in rap, trap, and contemporary urban music. Ideal for reaching engaged hip-hop culture fans.',
        tags: [
            { id: 1, title: 'Modern Rap' },
            { id: 2, title: 'Trap Music' },
            { id: 3, title: 'New School Hip-hop' },
            { id: 4, title: 'Urban Culture' },
            { id: 5, title: 'Street Fashion' },
        ]
    }
]

const tikTokStartersInit = [
    'Hip-hop Enthusiasts',
    'Gen Z',
    'Trending Music',
    'Music Lovers',
    'Urban Culture',
]

interface TargetingCampaignView_AUDIENCESProps {
    className?: string;
    platform: 'tiktok' | 'facebook'
    hideTitle?: boolean
}

const TargetingCampaignView_AUDIENCES = ({
    className,
    platform = 'tiktok',
    hideTitle = false
}: TargetingCampaignView_AUDIENCESProps) => {
    const {
        isLoadingMaestroRecommendations,
        selectedAudienceGroups,
        setSelectedAudienceGroups,
        addInterestToAudienceGroup,
        removeInterestFromAudienceGroup,
        conversionsEnabled,
        conversionsEventName,
        playbookType,
        usesExpressSetup
    } = useCampaignBuilder()
    const { currentBrand, isProBrand } = useBrand() as BrandExistsState
    const { loggedInFacebookUser, selectedFacebookPixel: facebookPixel, selectedFacebookAdAccount, selectedFacebookPage } = useFacebookConnections()
    const { handleOpenModal } = useContext(UpgradeModalContext);

    // Add an effect to ensure we re-render when the pixel connection status changes
    const hasPixelConnected = Boolean(facebookPixel?.id) && conversionsEnabled

    // Determine if user is on express flow
    const isExpressFlow = usesExpressSetup()

    // Determine if user has connected their ad account and page (not express flow)
    const hasConnectedAdAccountAndPage = Boolean(selectedFacebookAdAccount && selectedFacebookPage) && !isExpressFlow

    // Get the ad platform from playbook type
    const adPlatform = playbookType ? PLAYBOOK_TYPE_INFO[playbookType]?.adPlatform : null

    const getAllCustomAudiences = async (fbAdAccount: {
        id: string
    }, after: string | null = null): Promise<FBCustomAudience[]> => {
        let params = {
            fields: 'name,id,rule,rule_aggregation,description,delivery_status,lookalike_spec,data_source,subtype,approximate_count_lower_bound,approximate_count_upper_bound,is_value_based,opt_out_link',
            limit: 200,
            after,
            access_token: loggedInFacebookUser?.access_token,
        };

        const response = await queryFB(`/${fbAdAccount.id}/customaudiences`, { params });
        if (response.data && response.paging && response.paging.cursors && response.paging.cursors.after) {
            let returned = await getAllCustomAudiences(fbAdAccount, response.paging.cursors.after)
            if (!returned) return response.data;
            return response.data.concat(returned);
        } else {
            return response.data;
        }
    }

    const getDefaultTargetingTabsForPlatform = () => {
        if (platform === 'tiktok') {
            return [
                {
                    id: 'interestTargeting',
                    defaultAudienceGroup: 'interestTargeting',
                    title: 'Search TikTok',
                }
            ]
        } else if (platform === 'facebook') {
            let items = []

            if (isProBrand) {
                items.push({
                    id: 'explorePresets',
                    defaultAudienceGroup: 'explorePresets',
                    title: 'Recommendations',
                })
            }
            items.push(...[
                {
                    id: 'searchSimilarArtists',
                    defaultAudienceGroup: 'similarArtists',
                    title: 'Search Similar Artists',
                },
                {
                    id: 'interestTargeting',
                    defaultAudienceGroup: 'interestTargeting',
                    title: 'Search Facebook',
                },
                {
                    id: 'importAudience',
                    defaultAudienceGroup: 'importAudience',
                    title: 'Import Audiences',
                }
            ])

            return items
        }

        return []
    }

    const AUDIENCES_TABS = getDefaultTargetingTabsForPlatform()

    const [selectedAudienceTab, setSelectedAudienceTab] = useState<TabItem>(AUDIENCES_TABS[0])

    // For express flow, recommended audiences should not be editable
    const [isEditable, setIsEditable] = useState<boolean>(true)

    // State for Ask AI
    const [showAskAiModal, setShowAskAiModal] = useState(false)
    const [isLoadingAskAi, setIsLoadingAskAi] = useState(false)
    const [tikTokStarters, setTikTokStarters] = useState<string[]>(tikTokStartersInit)

    // State for Platform Search
    const [searchPlatformText, setSearchPlatformText] = useState('')
    const [searchPlatformInterestsResults, setSearchPlatformInterestsResults] = useState<SearchPlatformInterestsResult[]>([])
    const [isLoadingSearchPlatformInterests, setIsLoadingSearchPlatformInterests] = useState(false)

    // State for Search Similar Artists
    const [searchSimilarArtistsText, setSearchSimilarArtistsText] = useState('')
    const [searchSimilarArtistsResults, setSearchSimilarArtistsResults] = useState<SpotifyArtistSearchResponse[]>([])
    const [isLoadingSearchSimilarArtists, setIsLoadingSearchSimilarArtists] = useState(false)
    const [findingRelatedArtistsForSelectedArtist, setFindingRelatedArtistsForSelectedArtist] = useState<boolean>(false)
    const [relatedTargetingInterestsFromArtist, setRelatedTargetingInterestsFromArtist] = useState<FbSuggestion[]>([])
    const [selectedSimilarArtist, setSelectedSimilarArtist] = useState<SpotifyArtistSearchResponse | null>(null)
    const [selectedArtistName, setSelectedArtistName] = useState<string>('')

    // import audiences
    const [showImportAudiencesFromAdAccountModal, setShowImportAudiencesFromAdAccountModal] = useState(false)
    const [showImportAudiencesFromPagesModal, setShowImportAudiencesFromPagesModal] = useState(false)

    // import custom audiences
    const [loadingCustomAudiences, setLoadingCustomAudiences] = useState(false);
    const [customAudiences, setCustomAudiences] = useState<FBCustomAudience[]>([]);
    const [selectedCustomAudiences, setSelectedCustomAudiences] = useState<FBCustomAudience[]>([]);



    // Handle adding a preset audience with its associated tags
    const handleAddPresetAudienceWithTags = (preset: CreativeSelectorPost) => {
        if (!preset || !preset.id) {
            console.error('Invalid preset object:', preset)
            return
        }

        // Check if this preset already exists as a separate audience group
        // Look for both prefixed and non-prefixed versions
        const audienceGroupExists = selectedAudienceGroups.some(group =>
            group.id === `preset_${preset.id}` || group.id === preset.id
        )

        if (audienceGroupExists) {
            // If it exists, remove the entire audience group (both formats)
            const updatedGroups = selectedAudienceGroups.filter(group =>
                group.id !== `preset_${preset.id}` && group.id !== preset.id
            )
            setSelectedAudienceGroups(updatedGroups)
        } else {
            // Find the matching audience group from shared configs
            let audienceConfig;

            switch (preset.id) {
                case SHARED_AUDIENCE_CONFIGS.CONVERSION_EVENT_LOOKALIKES.id:
                    audienceConfig = { ...SHARED_AUDIENCE_CONFIGS.CONVERSION_EVENT_LOOKALIKES };
                    break;
                case SHARED_AUDIENCE_CONFIGS.CURRENT_FANS.id:
                    // Use the original ID format without prefix for consistency
                    audienceConfig = { ...SHARED_AUDIENCE_CONFIGS.CURRENT_FANS };
                    break;
                case SHARED_AUDIENCE_CONFIGS.SIMILAR_FANS.id:
                    audienceConfig = { ...SHARED_AUDIENCE_CONFIGS.SIMILAR_FANS };
                    break;
                default:
                    // Fallback to creating a new audience group with generated IDs
                    const newAudienceGroup: TargetingAudienceGroup = {
                        // Use the original ID without prefix for consistency
                        id: preset.id,
                        title: preset.title,
                        tags: []
                    }

                    // Add the main audience tag
                    newAudienceGroup.tags.push({
                        id: parseInt(preset.id) || Math.floor(Math.random() * 100000),
                        title: preset.title,
                        type: 'customAudience' as const
                    })

                    // Also add associated tags if they exist
                    if (preset.tags && preset.tags.length > 0) {
                        preset.tags.forEach((tag, index) => {
                            newAudienceGroup.tags.push({
                                id: parseInt(`${preset.id}${index}`) || Math.floor(Math.random() * 100000) + index,
                                title: tag,
                                type: 'customAudience' as const
                            })
                        })
                    }

                    // Add the new audience group to the existing groups
                    setSelectedAudienceGroups([...selectedAudienceGroups, newAudienceGroup])
                    return;
            }

            // If we found a matching audience config, use it
            if (audienceConfig) {
                setSelectedAudienceGroups([...selectedAudienceGroups, audienceConfig])
            }
        }
    }

    useEffect(() => {
        if (searchPlatformText === '') {
            setSearchPlatformInterestsResults([])
            setIsLoadingSearchPlatformInterests(false)
        }
    }, [searchPlatformText])

    useEffect(() => {
        if (selectedSimilarArtist) {
            handleFindSimilarArtist(selectedSimilarArtist)
        }
    }, [selectedSimilarArtist])

    // Reset artist selection state when changing tabs
    useEffect(() => {
        // Clear artist-related states when not in the search similar artists tab
        if (selectedAudienceTab.id !== 'searchSimilarArtists') {
            setSelectedSimilarArtist(null)
            setSelectedArtistName('')
            setRelatedTargetingInterestsFromArtist([])
            setSearchSimilarArtistsText('')
        }
    }, [selectedAudienceTab])

    // Filter preset audiences based on platform
    const defaultPresetAudiences = Object.keys(SHARED_AUDIENCE_CONFIGS)
        .map(key => SHARED_AUDIENCE_CONFIGS[key as keyof typeof SHARED_AUDIENCE_CONFIGS])
        .filter(preset => {
            // Platform-specific filtering
            if (adPlatform === AdPlatform.TIKTOK || platform === 'tiktok') {
                // For TikTok campaigns, only show TikTok recommended audiences
                return preset.id === SHARED_AUDIENCE_CONFIGS.TIKTOK_RECOMMENDED_AUDIENCES.id
            } else if (adPlatform === AdPlatform.META || platform === 'facebook') {
                // For META campaigns, hide TikTok recommended audiences
                return preset.id !== SHARED_AUDIENCE_CONFIGS.TIKTOK_RECOMMENDED_AUDIENCES.id
            }
            return true
        })
        .map(preset => ({
            ...preset,
            id: preset.id,
            backgroundColor: Colors.purpleLighter,
            title: preset.title,
            description: preset.description,
            tags: preset.tags.map(tag => tag.title)
        }))
    // Set default selected audiences when component mounts
    useEffect(() => {
        // Only set default audiences if none are already selected and we're in editable mode
        if (selectedAudienceGroups.length === 0 && isEditable) {
            let defaultAudienceIds = []

            // For TikTok campaigns, use TikTok recommended audiences
            if (adPlatform === AdPlatform.TIKTOK || platform === 'tiktok') {
                defaultAudienceIds = [SHARED_AUDIENCE_CONFIGS.TIKTOK_RECOMMENDED_AUDIENCES.id]
            } else {
                // For META campaigns, use FB + IG engagers and lookalikes
                defaultAudienceIds = [
                    SHARED_AUDIENCE_CONFIGS.CURRENT_FANS.id,
                    SHARED_AUDIENCE_CONFIGS.SIMILAR_FANS.id,
                ]

                // Add Conversion Event Lookalikes based on playbook configuration and setup type
                const playbookInfo = playbookType ? PLAYBOOK_TYPE_INFO[playbookType] : null;
                const playbookSupportsConversions = playbookInfo?.hasConversions === true;

                if (playbookSupportsConversions) {
                    // Custom setup: Only include if user has connected their own pixel and enabled conversions
                    if (conversionsEnabled && facebookPixel?.id) {
                        defaultAudienceIds.push(SHARED_AUDIENCE_CONFIGS.CONVERSION_EVENT_LOOKALIKES.id)

                    }
                }
            }

            // Apply each default audience
            defaultAudienceIds.forEach(audienceId => {
                const audience = defaultPresetAudiences.find(a => a.id === audienceId)
                if (audience) {
                    handleAddPresetAudienceWithTags(audience)
                }
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const transformInterestSearchResults = (results: DataFBQueryAdInterest): SearchPlatformInterestsResult[] => {
        if (platform === 'tiktok') {
            return results.map((result) => {
                // Check if the result is a hashtag by looking for # at the beginning
                const isHashtag = result.name.startsWith('#');
                return {
                    id: result.id,
                    name: result.name,
                    audience_size: result.audience_size || 0,
                    type: isHashtag ? 'hashtagAudience' : 'interestTargetingAudience'
                }
            })
        } else if (platform === 'facebook') {
            return results.map((result) => ({
                id: result.id,
                name: result.name,
                audience_size: result.audience_size || 0,
                type: 'interestTargetingAudience'
            }))
        }
        return []
    }

    const handleSearchPlatformInterests = async () => {
        // Don't search if the search text is empty
        if (!searchPlatformText.trim()) {
            setSearchPlatformInterestsResults([])
            return
        }

        try {
            setIsLoadingSearchPlatformInterests(true)

            if (platform === 'tiktok') {
                const { data, error } = await searchTiktokTargetingInterests_api(currentBrand.id, searchPlatformText, true)

                if (!data || (data.interests.length === 0 && data.hashtags.length === 0)) {
                    // Set empty results but keep track that a search was performed
                    setSearchPlatformInterestsResults([])
                } else {
                    // Transform TikTok interests to match the expected format
                    const allResults: TikTokSearchPlatformInterestsResult[] = [];

                    // Process interests
                    const interestResults = data.interests.map(interest => ({
                        id: interest.id,
                        name: interest.name,
                        audience_size: 0, // TikTok API doesn't provide this information
                        type: 'interestTargetingAudience' as const,
                        category: interest.category
                    }));

                    // Process hashtags
                    const hashtagResults = data.hashtags.map(hashtag => ({
                        id: hashtag.id,
                        name: hashtag.name,
                        audience_size: 0,
                        type: 'hashtagAudience' as const,
                        category: 'Hashtag'
                    }));

                    // Combine results
                    allResults.push(...interestResults, ...hashtagResults);
                    setSearchPlatformInterestsResults(allResults);
                }
            } else if (platform === 'facebook') {
                const results = await searchMaestroFacebookInterests_api(currentBrand.id, searchPlatformText)
                const interests = results.interests || []

                if (interests.length === 0) {
                    // Set empty results but keep track that a search was performed
                    setSearchPlatformInterestsResults([])
                } else {
                    setSearchPlatformInterestsResults(transformInterestSearchResults(interests))
                }
            }
        } catch (error) {
            console.error('error', error)
            // On error, still set empty results but log the error
            setSearchPlatformInterestsResults([])
        } finally {
            setIsLoadingSearchPlatformInterests(false)
        }
    }

    const handleAddSearchPlatformInterest = (interest: SearchPlatformInterestsResult) => {
        /** Get the current tab's default audience group to add the interest to */
        const currentTabAudienceGroupId = AUDIENCES_TABS.find(
            tab => tab.id === selectedAudienceTab.id
        )?.defaultAudienceGroup

        if (currentTabAudienceGroupId) {
            // For TikTok interests, include the category in the display title if available
            if (platform === 'tiktok' && (interest as TikTokSearchPlatformInterestsResult).category) {
                // Create a new interest object that includes the category in the name
                const tikTokInterest = interest as TikTokSearchPlatformInterestsResult
                const enhancedInterest = {
                    ...interest,
                    name: `${interest.name} (Category: ${tikTokInterest.category})`
                }
                // Convert string id to number since addInterestToAudienceGroup expects a number
                const interestId = parseInt(interest.id)
                if (!isNaN(interestId)) {
                    addInterestToAudienceGroup(currentTabAudienceGroupId, enhancedInterest)
                }
            } else {
                // Convert string id to number since addInterestToAudienceGroup expects a number
                const interestId = parseInt(interest.id)
                if (!isNaN(interestId)) {
                    addInterestToAudienceGroup(currentTabAudienceGroupId, interest)
                }
            }
        }
    }

    const handleAddSearchSimilarArtist = (artist: SpotifyArtistSearchResponse) => {
    }

    const renderApplyAllSection = () => {
        if (!isEditable) {
            return null
        }

        return (
            <div className="recommendationHeaderAndButtonContainer">
                <div className="recommendationHeader" style={{ marginBottom: 8 }}>
                    We recommend targeting the following audiences:
                </div>
                {/* <ButtonSymphony
                    className="recommendationButton"
                    onClick={applyAll}
                    variant="outlined"
                >
                    Apply All
                </ButtonSymphony> */}
            </div>
        )
    }

    const renderAudienceCards = () => {
        return (audiencesData.map((audience, index) => {
            return (
                <MaestroAdCard
                    key={makeKey('audience-card', audience.id, index)}
                    className="audienceCard"
                    title={audience.title}
                    description={audience.description}
                    tags={audience.tags as Tag[]}
                    onClick={isEditable ? () => { } : undefined}
                />
            )
        }))
    }

    const renderRecommendationsTab = () => {
        return (
            <TabSymphony
                className="targetingCampaignViewAudiencesComponentTab"
                visible={selectedAudienceTab.id === 'recommendations'}
                style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
            >
                {renderApplyAllSection()}
                {renderAudienceCards()}
            </TabSymphony>
        )
    }

    const renderAskAiTab = () => {

        return (
            <div className="targetingCampaignViewAudiencesComponentAskAiTab" style={sty.targetingCampaignViewAudiencesComponentAskAiTab}>
                <div className="targetingCampaignViewAudiencesComponentAskAiTabTitle" style={{ marginBottom: 8 }}>
                    Search for audiences with Maestro AI - craft the perfect audiences for your ad based on your selected content.
                </div>
                <ButtonSymphony
                    isLoading={isLoadingAskAi}
                    className="targetingCampaignViewAudiencesComponentAskAiTabButton"
                    onClick={() => setShowAskAiModal(true)}
                >
                    Search Audiences
                </ButtonSymphony>
            </div>
        )
    }

    const renderSearchPlatformTab = () => {

        let header = 'Search interests and categories with TikTok\'s Ads library'
        let placeholder = 'Search for interests or categories...'
        let noResultsMessage = 'No interests found within TikTok\'s targeting library for this search term.'

        if (platform === 'facebook') {
            header = 'Search interests and topics with Facebook\'s Ads library.'
            placeholder = 'Who are you looking to target?'
            noResultsMessage = 'No interests found within Facebook\'s targeting library for this search term.'
        }

        // Get current tab's audience group ID
        const currentTabAudienceGroupId = AUDIENCES_TABS.find(
            tab => tab.id === selectedAudienceTab.id
        )?.defaultAudienceGroup

        // Get the selected interests for this tab's audience group
        const currentAudienceGroup = selectedAudienceGroups.find(
            group => group.id === currentTabAudienceGroupId
        )

        // Convert selected interests to the same format as search results for consistency
        const selectedInterests: SearchPlatformInterestsResult[] = currentAudienceGroup?.tags.map(tag => {
            // Extract audience size from tag title if available (format: "Name (Audience Size: 123456)")
            let audienceSize = 0;
            const audienceSizeMatch = tag.title.match(/Audience Size: ([\d,]+)/);
            if (audienceSizeMatch && audienceSizeMatch[1]) {
                audienceSize = parseInt(audienceSizeMatch[1].replace(/,/g, ''));
            }

            // Ensure we're using a valid type for SearchPlatformInterestsResult
            let interestType: "interestTargetingAudience" | "hashtagAudience" = 'interestTargetingAudience';
            if (tag.type === 'hashtagAudience') {
                interestType = 'hashtagAudience';
            }

            return {
                id: tag.id.toString(),
                name: tag.title.split(' (Audience Size:')[0], // Get name without the audience size part
                audience_size: audienceSize,
                type: interestType
            };
        }) || [];

        // Filter the search results to show only unselected interests
        const unselectedSearchResults = searchPlatformInterestsResults.filter(
            result => !selectedInterests.some(tag => tag.id === result.id)
        )

        // Handle the case when search was performed but no results were found
        const searchPerformedWithNoResults =
            !isLoadingSearchPlatformInterests &&
            searchPlatformText.length > 0 &&
            searchPlatformInterestsResults.length === 0

        return (
            <TabSymphony
                className="targetingCampaignViewAudiencesComponentTab"
                visible={selectedAudienceTab.id === 'interestTargeting'}
                style={{ flexDirection: 'column', gap: 8 }}
            >
                <div>
                    {header}
                </div>
                <InputSymphony
                    isLoading={isLoadingSearchPlatformInterests}
                    className="targetingCampaignViewAudiencesComponentSearchPlatformTextInput"
                    placeholder={placeholder}
                    value={searchPlatformText}
                    onChange={(value) => setSearchPlatformText(value as string)}
                    isSearch
                    onSearch={handleSearchPlatformInterests}
                    onEnter={handleSearchPlatformInterests}
                    disabled={!isEditable}
                />
                {isLoadingSearchPlatformInterests && <MaestroLoadingMessage
                    startLoading={isLoadingSearchPlatformInterests} />}

                {/* Display message when no results found */}
                {searchPerformedWithNoResults &&
                    <div className="audienceSearchNoResults" style={{ marginTop: 16 }}>
                        {noResultsMessage}
                    </div>
                }

                {/* Display unselected search results */}
                {unselectedSearchResults.length > 0 && (
                    <div className="audienceSearchResults" style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
                        {searchPlatformText.length > 0 && <div>Search results for "{searchPlatformText}":</div>}
                        <div style={{ display: 'flex', flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
                            {unselectedSearchResults.map((result, index) => {
                                // For TikTok, show category instead of audience size if available
                                let displayText = `${result.name}`

                                if (platform === 'tiktok' && (result as TikTokSearchPlatformInterestsResult).category) {
                                    const tikTokResult = result as TikTokSearchPlatformInterestsResult
                                    displayText = `${result.name} (Category: ${tikTokResult.category})`
                                } else {
                                    displayText = result.audience_size ? `${result.name} (Audience Size: ${millify(result.audience_size)})` : result.name
                                }

                                return (
                                    <TagSymphony

                                        key={makeKey('targeting-tag', result, index)}
                                        className="audienceSearchTag"
                                        title={displayText}
                                        onAdd={() => handleAddSearchPlatformInterest(result)}
                                    />
                                )
                            })}
                        </div>
                    </div>
                )}

                {/* Display selected interests */}
                {selectedInterests.length > 0 && (
                    <div className="audienceSearchSelectedInterests" style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
                        <div>Selected interests:</div>
                        <div style={{ display: 'flex', flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
                            {selectedInterests.map((interest, index) => {
                                // For TikTok, show category instead of audience size if available
                                let displayText = `${interest.name}`

                                if (platform === 'tiktok' && (interest as TikTokSearchPlatformInterestsResult).category) {
                                    const tikTokInterest = interest as TikTokSearchPlatformInterestsResult
                                    displayText = `${interest.name} (Category: ${tikTokInterest.category})`
                                } else {
                                    displayText = interest.audience_size ? `${interest.name} (Audience Size: ${millify(interest.audience_size)})` : interest.name
                                }

                                return (
                                    <TagSymphony
                                        key={makeKey('targeting-tag-selected', interest, index)}
                                        className="audienceSearchTagSelected"
                                        title={displayText}
                                        onRemove={() => handleRemoveSearchPlatformInterest(interest)}
                                    />
                                )
                            })}
                        </div>
                    </div>
                )}
            </TabSymphony>
        )
    }

    // Add this new function to handle removing search platform interests
    const handleRemoveSearchPlatformInterest = (interest: SearchPlatformInterestsResult) => {
        const currentTabAudienceGroupId = AUDIENCES_TABS.find(
            tab => tab.id === selectedAudienceTab.id
        )?.defaultAudienceGroup

        if (currentTabAudienceGroupId) {
            const interestId = parseInt(interest.id)
            if (!isNaN(interestId)) {
                removeInterestFromAudienceGroup(currentTabAudienceGroupId, interestId)
            }
        }
    }

    const handleSearchSimilarArtists = async () => {
        try {
            setIsLoadingSearchSimilarArtists(true)

            if (platform === 'tiktok') {
                setSearchSimilarArtistsResults([])
            } else if (platform === 'facebook') {
                const results = await searchSimilarArtists_api(currentBrand.id, searchSimilarArtistsText)
                console.log('results', results)
                setSearchSimilarArtistsResults(results)
            }
        } catch (error) {
            console.error('error', error)
        } finally {
            setIsLoadingSearchSimilarArtists(false)
        }
    }

    const handleFindSimilarArtist = async (artist: SpotifyArtistSearchResponse) => {
        console.log('findSimilarArtist', artist)
        try {
            setFindingRelatedArtistsForSelectedArtist(true)
            setSelectedArtistName(artist.name)
            const results = await findRelatedArtistsForSpotifyId_api(artist.id)
            console.log('results', results)
            setRelatedTargetingInterestsFromArtist(results.targetingAudiences)
        } catch (error) {
            console.error('error', error)
        } finally {
            setFindingRelatedArtistsForSelectedArtist(false)
        }
    }

    const handleAddRelatedTargetingInterestFromArtist = (interest: FbSuggestion) => {
        console.log('addRelatedTargetingInterestFromArtist', interest)
        /** Get the current tab's default audience group to add the interest to */
        const currentTabAudienceGroupId = AUDIENCES_TABS.find(
            tab => tab.id === selectedAudienceTab.id
        )?.defaultAudienceGroup

        if (currentTabAudienceGroupId) {
            addInterestToAudienceGroup(currentTabAudienceGroupId, {
                id: interest.id,
                name: interest.name,
                audience_size: interest.audience_size || 0,
                type: 'interestTargetingAudience'
            })
        }
    }

    /** Check if an interest is already selected in the current tab's audience group */
    const isInterestSelected = (interestId: string): boolean => {
        const currentTabAudienceGroupId = AUDIENCES_TABS.find(
            tab => tab.id === selectedAudienceTab.id
        )?.defaultAudienceGroup

        if (!currentTabAudienceGroupId) return false

        const currentAudienceGroup = selectedAudienceGroups.find(
            group => group.id === currentTabAudienceGroupId
        )

        if (!currentAudienceGroup) return false

        return currentAudienceGroup.tags.some(tag => tag.id.toString() === interestId)
    }

    const handleRemoveRelatedTargetingInterestFromArtist = (interest: FbSuggestion) => {
        const currentTabAudienceGroupId = AUDIENCES_TABS.find(
            tab => tab.id === selectedAudienceTab.id
        )?.defaultAudienceGroup

        if (currentTabAudienceGroupId) {
            removeInterestFromAudienceGroup(currentTabAudienceGroupId, parseInt(interest.id))
        }
    }

    const renderRelatedArtistsInterestsList = () => {
        if (!selectedArtistName || findingRelatedArtistsForSelectedArtist) {
            return null
        }

        if (relatedTargetingInterestsFromArtist.length === 0) {
            return (
                <div className="audienceSearchTikTokNoResults" style={{ marginTop: 16 }}>
                    No related artists found within Facebook's targeting library for {selectedArtistName}.
                </div>
            )
        }

        // Split into selected and unselected interests
        const unselectedInterests = relatedTargetingInterestsFromArtist.filter(
            interest => !isInterestSelected(interest.id)
        )

        const selectedInterests = relatedTargetingInterestsFromArtist.filter(
            interest => isInterestSelected(interest.id)
        )

        if (unselectedInterests.length === 0 && selectedInterests.length > 0) {
            return (
                <div className="audienceSearchTikTokResults" style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
                    <div>All similar artists for {selectedArtistName} have been added:</div>
                    <div style={{ display: 'flex', flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
                        {selectedInterests.map((result, index) => (
                            <TagSymphony
                                key={makeKey('targeting-tag-selected', result, index)}
                                className="audienceSearchTikTokTag audienceSearchTikTokTagSelected"
                                title={`${result.name} (Audience Size: ${millify(result.audience_size || 0)})`}
                                onRemove={() => handleRemoveRelatedTargetingInterestFromArtist(result)}
                            />
                        ))}
                    </div>
                </div>
            )
        }

        return (
            <div className="audienceSearchTikTokResults" style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
                <div>These are the similar artists that exist for Facebook ad targeting:</div>
                <div style={{ display: 'flex', flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
                    {unselectedInterests.map((result, index) => (
                        <TagSymphony
                            key={makeKey('targeting-tag', result, index)}
                            className="audienceSearchTikTokTag"
                            title={`${result.name} (Audience Size: ${millify(result.audience_size || 0)})`}
                            onAdd={() => handleAddRelatedTargetingInterestFromArtist(result)}
                        />
                    ))}
                </div>

                {selectedInterests.length > 0 && (
                    <>
                        <div style={{ marginTop: 8 }}>Selected artists:</div>
                        <div style={{ display: 'flex', flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
                            {selectedInterests.map((result, index) => (
                                <TagSymphony
                                    key={makeKey('targeting-tag-selected', result, index)}
                                    className="audienceSearchTikTokTag audienceSearchTikTokTagSelected"
                                    title={`${result.name} (Audience Size: ${millify(result.audience_size || 0)})`}
                                    onRemove={() => handleRemoveRelatedTargetingInterestFromArtist(result)}
                                />
                            ))}
                        </div>
                    </>
                )}
            </div>
        )
    }

    const renderSearchSimilarArtistsTab = () => {
        let header = 'Search Spotify to target genres, playlists, and fanbases of artists.'
        let placeholder = 'Artists within your Spotify library'

        return (
            <TabSymphony
                className="targetingViewSimilarArtistsTab"
                visible={selectedAudienceTab.id === 'searchSimilarArtists'}
                style={{ flexDirection: 'column', gap: 8 }}
            >
                <div>
                    {header}
                </div>
                <InputSymphony
                    isSelect
                    options={searchSimilarArtistsResults.map((result) => ({
                        value: result.id,
                        title: `${result.name}`,
                        description: `${millify(result.followers)} Spotify Followers`,
                        thumbnailUrl: result.thumbnail_url,
                        object: result
                    }))}
                    fetchSelectedOptions={handleSearchSimilarArtists}
                    isLoading={isLoadingSearchSimilarArtists}
                    className="targetingViewSimilarArtistsTabInput"
                    placeholder={placeholder}
                    onChange={(value) => setSearchSimilarArtistsText(value as string)}
                    value={searchSimilarArtistsText}
                    onOptionSelect={(option) => {
                        console.log('option', option)
                        setSelectedSimilarArtist(option.object as SpotifyArtistSearchResponse)
                        // Clear the input field after selection
                        setSearchSimilarArtistsText('')
                    }}
                    disabled={!isEditable}
                />
                {findingRelatedArtistsForSelectedArtist && <MaestroLoadingMessage
                    loadingMessages={[`Finding related artists similar to ${selectedArtistName}...`,
                        `Searching interests on Facebook...`,
                        `Verifying results...`]}
                    startLoading={findingRelatedArtistsForSelectedArtist} />}
                {renderRelatedArtistsInterestsList()}

            </TabSymphony>
        )
    }

    const renderExplorePresetsTab = () => {
        // Check if an audience is selected (has its own bucket) - improved to handle both formats
        const isAudienceSelected = (audienceId: string): boolean => {
            // Check both formats: direct ID match and 'preset_' prefixed format
            return selectedAudienceGroups.some(group =>
                group.id === `preset_${audienceId}` || group.id === audienceId
            )
        }

        // Define actions for the ItemSelector
        const presetAudienceActions: ItemSelectorAction[] = isExpressFlow ? [] : [
            {
                title: 'Add',
                titleSelected: 'Remove',
                buttonVariant: 'primary',
                buttonVariantSelected: 'outlined',
                buttonSize: 'small',
                onClick: (preset: CreativeSelectorPost) => {
                    // For custom setup, check if pixel is connected for conversion lookalikes
                    if (preset.id === SHARED_AUDIENCE_CONFIGS.CONVERSION_EVENT_LOOKALIKES.id &&
                        hasConnectedAdAccountAndPage && !hasPixelConnected) {
                        return;
                    }
                    handleAddPresetAudienceWithTags(preset);
                }
            }
        ]

        // Get all selected audience presets
        const getSelectedPresetAudiences = (): CreativeSelectorPost[] => {
            // Find all audience groups that are either prefixed with "preset_" or directly match the config ids
            const selectedIds = selectedAudienceGroups.map(group => {
                // Handle both formats: either with preset_ prefix or without
                return group.id.startsWith('preset_') ? group.id.replace('preset_', '') : group.id
            })

            // Map these IDs back to the original mockPresetAudiences objects
            return defaultPresetAudiences.filter(preset =>
                selectedIds.includes(preset.id)
            )
        }

        // Add "disabled" property to conversion event lookalikes based on connection status
        const processedPresetAudiences = defaultPresetAudiences.map(preset => {
            if (preset.id === SHARED_AUDIENCE_CONFIGS.CONVERSION_EVENT_LOOKALIKES.id) {
                // For express flow, conversion lookalikes are always enabled (default)
                if (isExpressFlow) {
                    return preset;
                }

                // For custom setup, check if pixel is connected
                if (hasConnectedAdAccountAndPage && !hasPixelConnected) {
                    return {
                        ...preset,
                        disabled: true,
                        description: 'Connect a Facebook Pixel to enable this audience group',
                        originalDescription: preset.description
                    };
                }
            }
            return preset;
        });

        const selectedPresetAudiences = getSelectedPresetAudiences()

        // Render the selected audience tags if any are selected
        const renderSelectedAudienceTags = () => {
            if (!selectedPresetAudiences || selectedPresetAudiences.length === 0) return null;

            return (
                <div className="selectedAudienceTagsContainer" style={sty.selectedAudienceTagsContainer}>
                    <div style={{ fontWeight: 600, marginBottom: 8 }}>Selected Audience Groups:</div>
                    {selectedPresetAudiences.map((audience, index) => {
                        // Skip rendering if audience is undefined
                        if (!audience) return null;
                        return (
                            <div
                                key={makeKey('selected-audience', index)}
                                className="selectedAudienceCard"
                                style={sty.selectedAudienceCard}
                            >
                                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <Icon
                                            className="maestroAudienceIcon"
                                            icon="maestroBadgeImage"
                                            size={24}
                                        />
                                        <div style={{ fontWeight: 500 }}>{audience.title}</div>
                                    </div>
                                    {!isExpressFlow && (
                                        <ButtonSymphony
                                            className="removeAudienceButton"
                                            variant="text"
                                            size="small"
                                            onClick={() => handleAddPresetAudienceWithTags(audience)}
                                        >
                                            Remove
                                        </ButtonSymphony>
                                    )}
                                </div>

                                <div style={{ marginTop: 8, color: Colors.greyDark }}>{audience.description}</div>

                                <div style={{ marginTop: 12, display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                                    {audience.tags?.map((tag, tagIndex) => (
                                        <TagSymphony
                                            key={makeKey('audience-tag', tagIndex)}
                                            title={tag}
                                            className="audienceTag"
                                        />
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            );
        };

        return (
            <TabSymphony
                className="targetingCampaignViewAudiencesComponentTab"
                visible={selectedAudienceTab.id === 'explorePresets'}
                style={{ gap: 16 }}
            >
                <CardSymphony
                    className="targetingCampaignViewAudiencesComponentExplorePresetsHeader"
                    title="Recommended Audiences"
                    subtitle={isExpressFlow
                        ? "These audiences are automatically optimized for your Express Setup campaign."
                        : "We think these audience groups will do the best job of reaching your target audience."
                    }
                    isNoPadding
                    size="small"
                />

                {isExpressFlow && (
                    <Banner
                        className="expressFlowAudienceBanner"
                        color="yellow"
                        title="Express Setup Active"
                    >
                        You're using Express Setup. These recommended audiences are optimized for your campaign and cannot be edited. To customize your audiences, connect your ad account in the setup flow.
                    </Banner>
                )}

                <ItemSelector
                    showTextOverlay={true}
                    className="targetingCampaignViewAudiencesComponentExplorePresetsItemSelector"
                    items={processedPresetAudiences}
                    selectedItems={selectedPresetAudiences}
                    isLoading={false}
                    onSelectItem={(item) => {
                        // Don't allow editing in express flow
                        if (isExpressFlow) {
                            return;
                        }

                        const preset = item as CreativeSelectorPost;
                        // Don't allow selecting the conversion lookalikes without a pixel for custom setup
                        if (preset.id === SHARED_AUDIENCE_CONFIGS.CONVERSION_EVENT_LOOKALIKES.id &&
                            hasConnectedAdAccountAndPage && !hasPixelConnected) {
                            return;
                        }
                        handleAddPresetAudienceWithTags(preset);
                    }}
                    actions={presetAudienceActions}
                    isMaestroStyle={true}
                    showViewButton={false}
                />

                {renderSelectedAudienceTags()}
            </TabSymphony>
        )
    }

    const handleImportAudiencesFromAdAccount = () => {
        try {
            console.log('importAudiencesFromAdAccount')
            setShowImportAudiencesFromAdAccountModal(true)
        } catch (error) {
            console.error('error', error)
        } finally {
        }
    }

    const handleImportAudiencesFromPages = () => {
        console.log('importAudiencesFromPages')
        setShowImportAudiencesFromPagesModal(true)
    }

    const renderImportAudiencesTab = () => {
        // Handle different scenarios for import audiences
        const getImportAudiencesContent = () => {
            // Scenario 1: User IS PRO
            if (isProBrand) {
                // Scenario 1a: Pro user on EXPRESS flow
                if (isExpressFlow) {
                    return (
                        <ButtonSymphony
                            className="targetingCampaignViewAudiencesComponentImportAudiencesButton"
                            variant="outlined"
                            disabled={true}
                        >
                            Connect your Ad Account to Import
                        </ButtonSymphony>
                    );
                }
                // Scenario 1b: Pro user NOT on express (has connected ad account + page)
                else if (hasConnectedAdAccountAndPage) {
                    return (
                        <ButtonSymphony
                            className="targetingCampaignViewAudiencesComponentImportAudiencesButton"
                            onClick={() => {
                                handleImportAudiencesFromAdAccount()
                            }}
                        >
                            Import Audiences from your Ad Account
                        </ButtonSymphony>
                    );
                }
            }

            // Scenario 2: User IS NOT PRO (free or lite)
            else {
                // Scenario 2a: Non-pro user on EXPRESS flow
                if (isExpressFlow) {
                    return (
                        <UpgradeToUnlockCTA
                            className="targetingCampaignViewAudiencesComponentImportAudiencesButton"
                            requiresPaidPlan={true}
                            paywallText="Connect your ad account and upgrade to Growth to import your custom audiences"
                            buttonText="Upgrade to Growth"
                            upgradeSource="importAudiences"
                            onlyShowPlans={[PlanType.GROWTH]}
                            highlightedPlan={PlanType.GROWTH}
                        />
                    );
                }
                // Scenario 2b: Non-pro user NOT on express (has connected ad account + page)
                else if (hasConnectedAdAccountAndPage) {
                    return (
                        <UpgradeToUnlockCTA
                            className="targetingCampaignViewAudiencesComponentImportAudiencesButton"
                            requiresPaidPlan={true}
                            paywallText="Upgrade to Growth to import your custom audiences"
                            buttonText="Upgrade to Growth"
                            upgradeSource="importAudiences"
                            onlyShowPlans={[PlanType.GROWTH]}
                            highlightedPlan={PlanType.GROWTH}
                        />
                    );
                }
            }

            // Default fallback case
            return (
                <ButtonSymphony
                    className="targetingCampaignViewAudiencesComponentImportAudiencesButton"
                    variant="outlined"
                    onClick={() => {
                        handleOpenModal({
                            source: 'Campaign Creation - Import Audiences Default',
                            highlightedPlan: PlanType.GROWTH,
                            upgradeCallToAction: 'Connect your ad account and upgrade to the Growth plan to import your custom audiences'
                        });
                    }}
                >
                    Upgrade to GROWTH
                </ButtonSymphony>
            );
        };

        return (
            <TabSymphony
                className="targetingCampaignViewAudiencesComponentTab"
                visible={selectedAudienceTab.id === 'importAudience'}
                style={{ gap: 8 }}
            >
                <CardSymphony
                    className="targetingCampaignViewAudiencesComponentImportAudiencesHeader"
                    title="Import Audiences"
                    subtitle={isExpressFlow
                        ? "Connect your ad account and upgrade to import existing audiences you've made."
                        : "Import existing audiences you've made."
                    }
                    isNoPadding
                    size="small"
                />
                {getImportAudiencesContent()}
            </TabSymphony>
        )
    }
    const renderAutomaticTargetingBanner = () => {
        if (isEditable) {
            return null
        }
        return (
            <Banner
                className="automaticTargetingBanner"
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 8,
                }}
            >
                <Icon
                    className="automaticaTargetingBannerIcon"
                    icon="robot"
                />
                <div style={Sty.text16purple}>
                    {isExpressFlow
                        ? "Express Setup: These audiences are automatically optimized for your campaign."
                        : "We've targeted these audiences for you automatically."
                    }
                </div>
                {!isExpressFlow && (
                    <ButtonSymphony
                        className="automaticTargetingBannerEditButton"
                        style={{
                            marginLeft: 'auto',
                        }}
                        onClick={() => setIsEditable(true)}
                        variant="outlined"
                    >
                        Edit
                    </ButtonSymphony>
                )}
            </Banner>
        )
    }

    if (isLoadingMaestroRecommendations) {
        return (
            <CardSymphony className="targetingCampaignViewAudiencesComponentLoadingAudience">
                <LoadingBar
                    message="Learning your content..."
                    percentage={50}
                />
                <LoadingBar
                    message="Analyzing fans..."
                    percentage={50}
                />
            </CardSymphony>
        )
    }

    const renderModals = () => {
        return (
            <>
                <AdConnectionModal
                    isOpen={showImportAudiencesFromAdAccountModal}
                    onClose={() => setShowImportAudiencesFromAdAccountModal(false)}
                    connectionType={AdConnectionType.FACEBOOK_CUSTOM_AUDIENCE}
                    allowMultipleSelection={true}
                    onImportMultiple={(audiences: FBCustomAudience[]) => {
                        const importedAudiencesGroupId = 'importedCustomAudiences';

                        const importedAudienceTags = audiences.map(audience => ({
                            id: parseInt(audience.id) || Math.floor(Math.random() * 100000),
                            title: audience.name,
                            type: 'customAudience' as const
                        }));

                        const existingGroup = selectedAudienceGroups.find(group => group.id === importedAudiencesGroupId);
                        if (existingGroup) {
                            const existingTagIds = new Set(existingGroup.tags.map(tag => Number(tag.id)));
                            const uniqueNewTags = importedAudienceTags.filter(tag => !existingTagIds.has(tag.id));

                            setSelectedAudienceGroups(selectedAudienceGroups.map(group =>
                                group.id === importedAudiencesGroupId
                                    ? { ...group, tags: [...group.tags, ...uniqueNewTags] }
                                    : group
                            ));
                        } else {
                            const newGroup: TargetingAudienceGroup = {
                                id: importedAudiencesGroupId,
                                title: 'Imported Custom Audiences',
                                tags: importedAudienceTags
                            };

                            setSelectedAudienceGroups([...selectedAudienceGroups, newGroup]);
                        }
                    }}
                />
            </>
        )
    }

    // NOTE: render()
    return (
        <div
            className={clsx('targetingCampaignView_AUDIENCESComponent', className)}
            style={sty.targetingCampaignView_AUDIENCESComponent}
        >
            {/* <DebugBox
                alwaysShow
                data={{
                    conversionsEnabled,
                    selectedAudienceGroups,
                    facebookPixel,
                    hasPixelConnected
                }}
            /> */}
            <CardSymphony
                className="targetingCampaignViewAudiencesComponentHeader"
                title={hideTitle ? '' : 'Audiences'}
                subtitle={hideTitle ? '' : 'Refine the audiences you want to target with your marketing efforts.'}
                isNoPadding
                size="small"
            />
            {isEditable &&
                <TabsSymphony
                    className="targetingCampaignViewAudiencesComponentTabs"
                    tabs={AUDIENCES_TABS}
                    selectedTab={selectedAudienceTab}
                    onChange={(tab) => setSelectedAudienceTab(tab as TabItem)}
                    style={{ overflowX: 'scroll' }}
                    isBorderBottom
                />
            }
            {/* {renderRecommendationsTab()} */}
            {renderSearchPlatformTab()}
            {renderSearchSimilarArtistsTab()}
            {renderExplorePresetsTab()}
            {renderImportAudiencesTab()}
            {renderAutomaticTargetingBanner()}
            {renderModals()}
            {/* <ModalSymphony
                className="writeIdeaModal"
                isOpen={showAskAiModal}
                title="Maestro AI will help you"
                subtitle="What do you want to communicate?"
                headerIcon="maestroBadgeImage"
                onClose={() => setShowAskAiModal(false)}
                style={{
                    gap: 16,
                }}
            >
                <InputSymphony
                    className="targetingCampaignViewAudiencesComponentAskAiTextInput"
                    placeholder="Ask away..."
                    value={askAiText}
                    isLoading={isLoadingAskAi}
                    onChange={(value) => setAskAiText(value as string)}
                    isSearch
                    onSearch={handleAskAiSearch}
                    onEnter={handleAskAiSearch}
                />
                <ButtonSymphony
                    isLoading={isLoadingAskAi}
                    className="writeIdeaModalButton"
                    onClick={handleAskAiSearch}
                >
                    Generate
                </ButtonSymphony>
            </ModalSymphony> */}
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    targetingCampaignView_AUDIENCESComponent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        backgroundColor: 'white',
        borderRadius: 8,
    },
    askAiTikTokStarters: {
        display: 'flex',
        flexDirection: 'row',
        gap: 8,
        flexWrap: 'wrap',
    },
    targetingCampaignViewAudiencesComponentAskAiTab: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        backgroundColor: Colors.purpleLightest,
        borderRadius: 8,
    },
    selectedAudienceTagsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },
    selectedAudienceCard: {
        padding: 8,
        border: Sty.border,
        borderRadius: 8,
    },
    importAudiencesContainer: {
        padding: 16,
    },
    importAudiencesHeader: {
        marginBottom: 16,
    },
    audiencesList: {
        marginBottom: 16,
    },
    importButtonContainer: {
        textAlign: 'right',
    },
    pixelRequirementBanner: {
        backgroundColor: Colors.purpleLightest,
        border: `1px solid ${Colors.purpleLight}`,
        padding: 8,
        borderRadius: 4,
        marginBottom: 16,
    },
}

export default TargetingCampaignView_AUDIENCES